import React, { useLayoutEffect } from 'react';
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

import { MMUStatusChanged } from 'src/app/redux/actions/socket.action';
import { reduxRootTypes } from 'src/types';
import { EMMUConnectionStatus, MMUConnectionStatus } from 'src/types/components/mmu.type';
import { mmuHelper, socketHelper, storageHelper } from 'src/app/helpers';

import { useMMUSocket } from 'src/app/hooks/useMMUSocket';
import { useSocket } from 'src/app/hooks/UseSocket';
import { UseHymotseSocket } from 'src/app/hooks/UseHymotseSocket';
import { eventActions, socketActions } from 'src/app/redux/actions';
import { GameState } from 'src/enums/game-state.enum';
import { connectionRequest } from 'src/app/helpers/socket.helper';
import { forwardingRequest } from 'src/app/helpers/mmu.helper';
import { urlsConfig } from 'src/configs';
import notification from 'antd/es/notification';

const MMUConnectionHandler = () => {
  const dispatch = useDispatch();

  const { id: userId, token, authenticated } = useSelector((state: reduxRootTypes.RootStateType) => state.userState);

  const { hymotsDB } = useSelector((state: reduxRootTypes.RootStateType) => state.eventState);
  const { isSurferOn, MMUStatus, surferAddress } = useSelector(
    (state: reduxRootTypes.RootStateType) => state.socketState,
  );

  const { units } = useSelector((state: reduxRootTypes.RootStateType) => state.unitsState);

  const openNotification = useCallback((title: string, context: string, isError: boolean) => {
    notification.info({
      message: title,
      description: context,
      duration: 3,
      placement: 'bottomLeft',
      icon: isError ? (
        <CloseCircleOutlined style={{ color: 'red' }} />
      ) : (
        <CheckCircleOutlined style={{ color: 'var(--ete-primary-green-color)' }} />
      ),
    });
  }, []);

  const { socketListenerHandler } = useSocket({ openNotification });
  const { MMUListenerHandler } = useMMUSocket({ openNotification });
  const { hymotseSocketListenerHandler } = UseHymotseSocket();

  const connectToMMU = (currentStatus: MMUConnectionStatus) => {
    if (userId) {
      mmuHelper.createConnection((status: boolean) => {
        if (
          !(
            (status && currentStatus === EMMUConnectionStatus.CONNECTED) ||
            (!status && currentStatus === EMMUConnectionStatus.DISCONNECTED)
          )
        ) {
          dispatch(
            socketActions.MMUStatusChanged(status ? EMMUConnectionStatus.CONNECTED : EMMUConnectionStatus.DISCONNECTED),
          );

          if (status === false) {
            dispatch(eventActions.eventStateChanged(GameState.Failed));
          }
        }
      });
    }
  };

  useEffect(() => {
    let reconnectInterval: NodeJS.Timeout | null = null;

    if (MMUStatus === EMMUConnectionStatus.DISCONNECTED) {
      reconnectInterval = setInterval(() => {
        mmuHelper.disconnect();
        connectToMMU(MMUStatus);
      }, 5000);
    }

    return () => {
      if (reconnectInterval) clearInterval(reconnectInterval);
    };
  }, [MMUStatus]);

  useEffect(() => {
    if (hymotsDB) {
      mmuHelper.disconnect();
      dispatch(MMUStatusChanged(EMMUConnectionStatus['NOT CONNECTED']));
    }
  }, [hymotsDB]);

  useEffect(() => {
    // TODO If exists, stop periodic retry
    if (token && authenticated && userId) {
      switch (MMUStatus) {
        case EMMUConnectionStatus['NOT CONNECTED']:
          connectToMMU(MMUStatus);
          break;

        case EMMUConnectionStatus.DISCONNECTED:
          // TODO Implement periodic retry
          break;

        case EMMUConnectionStatus.CONNECTED:
          mmuHelper.handshakeRequest(hymotsDB!);
          break;

        case EMMUConnectionStatus.HANDSHAKED:
          mmuHelper.forwardingRequest(hymotsDB!);
          break;

        default:
          break;
      }
    }
  }, [MMUStatus]);

  useEffect(() => {
    if (isSurferOn !== undefined) {
      if (
        !isSurferOn &&
        hymotsDB &&
        (MMUStatus === EMMUConnectionStatus.CONNECTED || MMUStatus === EMMUConnectionStatus.HANDSHAKED)
      ) {
        forwardingRequest(hymotsDB);
      }
    }
  }, [isSurferOn, MMUStatus, hymotsDB]);

  useEffect(() => {
    if (surferAddress && isSurferOn) {
      socketHelper.disconnect();
      socketHelper.createConnection(surferAddress, (socketStatus: boolean) => {
        dispatch(socketActions.surferStateChanged(socketStatus));

        if (socketStatus === false) {
          dispatch(eventActions.eventStateChanged(GameState.Failed));
        } else {
          if (userId && hymotsDB) connectionRequest(hymotsDB);
        }
      });
    }
  }, [surferAddress, userId, hymotsDB]);

  useEffect(() => {
    if (token && authenticated && MMUStatus === EMMUConnectionStatus.HANDSHAKED && surferAddress && units.length > 0) {
      if (userId) {
        socketHelper.createConnection(surferAddress, (socketStatus: boolean) => {
          dispatch(socketActions.surferStateChanged(socketStatus));

          if (socketStatus === false) {
            dispatch(eventActions.eventStateChanged(GameState.Failed));
          } else {
            if (userId && hymotsDB) connectionRequest(hymotsDB);
          }
        });
      }
    }
  }, [MMUStatus, surferAddress, token, units, authenticated]);

  useEffect(() => {
    if (isSurferOn !== undefined && userId) {
      if (!isSurferOn) {
      }
    }
  }, [isSurferOn]);

  useEffect(() => {
    const veritabaniId = storageHelper.getValueWithKey('veritabaniId');
    if (hymotsDB && hymotsDB.toString() === veritabaniId) {
      socketListenerHandler();
      MMUListenerHandler();
      hymotseSocketListenerHandler();
    }
  }, [socketListenerHandler, MMUStatus, MMUListenerHandler, hymotseSocketListenerHandler]);

  return null;
};

export default MMUConnectionHandler;
